import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import makeStyles from '@material-ui/styles/makeStyles';
import { Fragment, useMemo, useState } from 'react';
import Button from '@material-ui/core/Button';
import { primaryColor } from 'assets/jss/material-dashboard-pro-react';

const useStyles = makeStyles({
  root: {
    textAlign: 'left',
  },
  line: {
    borderColor: 'RGB(219,219,219)',
  },
  dash: {
    marginLeft: '0.2em',
  },
  heading: {
    marginTop: 0,
    marginBottom: '24px',
  },
  button: {
    display: 'inline',
    color: primaryColor[0],
    fontSize: '14px',
    textTransform: 'capitalize',
    textDecoration: 'underline',
    fontWeight: '400',
  },
  paragraph: {
    fontSize: '14px',
    fontWeight: '300',
    marginLeft: '16px',
  },
  span: {
    fontWeight: 'bold',
  },
});

const keysWithValuesAsArray = [
  'brands',
  'zoneCategories',
  'subBrands',
  'recipeTags',
  'mealCategories',
  'types',
  'drivers',
  'kitchen',
  'department',
  'employee',
  'zones',
  'multiplierMatrix',
];

const standAloneKeys = ['eachRecipeOnNewPage'];

const FiltersModal = ({ filters = {}, options = {}, recurrent = false }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const [isOpen, setIsOpen] = useState(false);

  const translations = useMemo(
    () => ({
      date: t('common.shared.date'),
      brands: t('reportArchive.filters.brands'),
      dateFrom: t('reportArchive.filters.dateFrom'),
      dateTo: t('reportArchive.filters.dateTo'),
      department: t('reportArchive.filters.department'),
      drivers: t('reportArchive.filters.drivers'),
      eachRecipeOnNewPage: t('reportArchive.filters.eachRecipeOnNewPage'),
      employee: t('reportArchive.filters.employee'),
      groupClient: t('reportArchive.filters.groupClient'),
      includeShopElements: t('reportArchive.filters.includeShopElements'),
      includeSubscriptions: t('reportArchive.filters.includeSubscriptions'),
      kitchen: t('reportArchive.filters.kitchen'),
      mealCategories: t('reportArchive.filters.mealCategories'),
      multiplier: t('reportArchive.filters.multiplier'),
      packedDate: t('reportArchive.filters.packedDate'),
      recipeTags: t('reportArchive.filters.recipeTags'),
      shouldBeConsumedTo: t('reportArchive.filters.shouldBeConsumedTo'),
      subBrands: t('reportArchive.filters.subBrands'),
      types: t('reportArchive.filters.types'),
      variant: t('reportArchive.filters.variant'),
      zoneCategories: t('reportArchive.filters.zoneCategories'),
      zones: t('reportArchive.filters.zones'),
      'delivery-point': t('reportArchive.filters.deliveryPoint'),
      multiplierMatrix: t('reportArchive.filters.multiplierMatrix'),
      individualForDiets: t('reportArchive.filters.individualForDiets'),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <div className={classes.root}>
      {!recurrent && (
        <h2 className={classes.heading}>
          {t('reportArchive.filters.heading')}
        </h2>
      )}
      {Object.entries(filters).map(([filterKey, filterValue]) => {
        if (filterKey === 'parameters') {
          return filterValue.map((nestedFilter, index, arr) => (
            <Fragment key={index}>
              <FiltersModal
                filters={nestedFilter}
                options={options}
                recurrent={true}
              />
              {index < arr.length - 1 && <hr className={classes.line} />}
            </Fragment>
          ));
        }

        if (!(filterKey in translations)) {
          return null;
        }

        let formattedFilterValue = filterValue;

        if (keysWithValuesAsArray.includes(filterKey)) {
          const separator =
            filterKey === 'recipeTags' && filters.recipeTagsOperator
              ? ` ${t(
                  `reportPreview.recipeTagsOperator.${filters.recipeTagsOperator}`
                )} `
              : ', ';
          formattedFilterValue = filterValue
            .map(value => {
              const matchedOption = options[filterKey]?.find(
                option => option['@id'] === value || option.value === value
              );
              return matchedOption?.name || matchedOption?.value || value;
            })
            .filter(optionName => optionName !== '*')
            .join(separator);
        }

        if (filterValue?.toString() === 'true') {
          formattedFilterValue = t('common.shared.yes').toUpperCase();
        }

        if (filterValue?.toString() === 'false') {
          formattedFilterValue = t('common.shared.no').toUpperCase();
        }

        if (filterKey === 'delivery-point') {
          if (filterValue?.toString() === 'true') {
            formattedFilterValue = t('reports.pickupPointDelivery');
          }

          if (filterValue?.toString() === 'false') {
            formattedFilterValue = t('brands.newBrandForm.toDoor');
          }
        }

        if (filterKey === 'variant') {
          const matchedOption = options.variants?.find(
            ({ id }) => Number(filterValue) === Number(id)
          );

          formattedFilterValue = matchedOption?.name || filterValue;
        }

        if (filterKey.includes('date')) {
          formattedFilterValue = format(new Date(filterValue), 'dd.MM.yyyy');
        }

        if (filterKey === 'multiplierMatrix') {
          formattedFilterValue = (
            <>
              <span>
                {t('reportArchive.filters.individualForDiets')}
                <Button
                  className={classes.button}
                  onClick={() => setIsOpen(prev => !prev)}
                >
                  {isOpen
                    ? t('reportArchive.filters.collapseList')
                    : t('reportArchive.filters.expandList')}
                </Button>
              </span>
              {isOpen &&
                filterValue.map(({ diets, shopDishes }, index) => (
                  <Fragment
                    key={`${
                      shopDishes?.multiplier || diets[index].diet
                    }-${index}`}
                  >
                    {shopDishes && (
                      <p className={classes.paragraph}>
                        {t('reportArchive.filters.shopDishes')}{' '}
                        <strong>{shopDishes.multiplier}</strong>
                      </p>
                    )}

                    {diets &&
                      diets.map(({ diet, calorific, multiplier }) => (
                        <p
                          className={classes.paragraph}
                          key={`${diet}-${calorific}`}
                        >
                          {`${diet} - ${calorific} ${t(
                            'reportArchive.filters.kcal'
                          )}: `}
                          <strong>{multiplier}</strong>
                        </p>
                      ))}
                  </Fragment>
                ))}
            </>
          );
        }

        return (
          <p key={filterKey}>
            {standAloneKeys.includes(filterKey) && (
              <hr className={classes.line} />
            )}
            <span>{translations[filterKey]}: </span>
            {formattedFilterValue ? (
              <b>{formattedFilterValue}</b>
            ) : (
              <span className={classes.dash}>-</span>
            )}
          </p>
        );
      })}
    </div>
  );
};

export default FiltersModal;
